import { useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api, formatApiErrorDetail } from "../lib/apiClient";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { ChefHat, ArrowRight } from "lucide-react";

const HERO = "https://images.pexels.com/photos/19836790/pexels-photo-19836790.jpeg";

export default function Login() {
  const { login } = useAuth();
  const [mode, setMode] = useState("login");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const endpoint = mode === "login" ? "/auth/login" : "/auth/register";
      const body = mode === "login" ? { email, password } : { email, password, name };
      const { data } = await api.post(endpoint, body);
      login(data.token, data.user);
    } catch (err) {
      setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Hero */}
      <div className="relative hidden lg:block">
        <img src={HERO} alt="Cucina di design" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-neutral-900/30" />
        <div className="absolute top-0 left-0 p-12">
          <div className="flex items-center gap-2 text-white">
            <ChefHat strokeWidth={1.5} className="h-6 w-6" />
            <span className="font-head text-lg tracking-tight">Cucine Expo</span>
          </div>
        </div>
        <div className="absolute bottom-0 left-0 p-12 max-w-md">
          <p className="font-head text-3xl font-light text-white leading-snug tracking-tight">
            Gestione esposizioni cucine per rivenditori.
          </p>
          <p className="mt-3 text-sm text-white/70">
            Modelli, finiture, agenti e stato di sviluppo — tutto in un unico posto.
          </p>
        </div>
      </div>

      {/* Form */}
      <div className="flex items-center justify-center p-8 lg:p-16">
        <div className="w-full max-w-sm">
          <div className="lg:hidden flex items-center gap-2 mb-10">
            <ChefHat strokeWidth={1.5} className="h-6 w-6 text-[#B4654A]" />
            <span className="font-head text-lg tracking-tight">Cucine Expo</span>
          </div>

          <p className="text-xs uppercase tracking-[0.2em] text-neutral-400">
            {mode === "login" ? "Bentornato" : "Crea account"}
          </p>
          <h1 className="font-head text-4xl font-light tracking-tight mt-2 text-neutral-900">
            {mode === "login" ? "Accedi" : "Registrati"}
          </h1>

          <form onSubmit={submit} className="mt-10 space-y-5">
            {mode === "register" && (
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.1em] text-neutral-500">Nome</Label>
                <Input
                  data-testid="register-name-input"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="rounded-none border-neutral-200 focus-visible:ring-0 focus-visible:border-neutral-900 h-11"
                  placeholder="Mario Rossi"
                />
              </div>
            )}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.1em] text-neutral-500">Email</Label>
              <Input
                data-testid="login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="rounded-none border-neutral-200 focus-visible:ring-0 focus-visible:border-neutral-900 h-11"
                placeholder="nome@azienda.it"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.1em] text-neutral-500">Password</Label>
              <Input
                data-testid="login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="rounded-none border-neutral-200 focus-visible:ring-0 focus-visible:border-neutral-900 h-11"
                placeholder="••••••••"
              />
            </div>

            {error && (
              <p data-testid="login-error" className="text-sm text-red-600">{error}</p>
            )}

            <Button
              data-testid="login-submit-button"
              type="submit"
              disabled={loading}
              className="w-full rounded-none h-11 bg-[#B4654A] hover:bg-[#98553E] text-white group"
            >
              {loading ? "Attendere…" : mode === "login" ? "Accedi" : "Crea account"}
              <ArrowRight className="ml-2 h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Button>
          </form>

          <p className="mt-8 text-sm text-neutral-500">
            {mode === "login" ? "Non hai un account? " : "Hai già un account? "}
            <button
              data-testid="toggle-auth-mode"
              onClick={() => { setMode(mode === "login" ? "register" : "login"); setError(""); }}
              className="text-neutral-900 underline underline-offset-4 hover:text-[#B4654A] transition-colors"
            >
              {mode === "login" ? "Registrati" : "Accedi"}
            </button>
          </p>

          <p className="mt-10 text-xs text-neutral-400 border-t border-neutral-100 pt-4">
            Demo: admin@cucine.it / admin123
          </p>
        </div>
      </div>
    </div>
  );
}
